import { Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '@common/enums/constants';
import { NavigationDirection } from '@common/enums/navigation-direction';
import { selectI18N } from '@common/store/i18n/selectors/i18n.selectors';
import { Store } from '@ngrx/store';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import {
    OpenFullscreenModalActions,
    selectCurrentFullscreenMediaUrl,
    selectCurrentFullscreenMediaUrlIndex,
    selectFullScreenMediaNames,
    selectFullscreenMediaUrls,
} from '@src/suite/common/store/fullscreen';
import { MediaType } from '@src/suite/discover/store/models/discover.models';
import { selectSelectedWire } from '@src/suite/discover/store/selectors/discover.selectors';

import { MediaComponent } from '../../media/media.component';

@Component({
    selector: 'st-media-fullscreen',
    imports: [MatIcon, MatIconButton, MediaComponent],
    templateUrl: './st-media-fullscreen.html',
    styleUrl: './st-media-fullscreen.scss',
    encapsulation: ViewEncapsulation.None,
})
export class MediaFullscreenComponent {
    public dialogRef = inject(MatDialogRef);
    protected store = inject(Store);

    NavigationDirection = NavigationDirection;
    languageSettings = this.store.selectSignal(selectI18N)();

    currentMediaUrls = this.store.selectSignal(selectFullscreenMediaUrls);
    currentMediaNames = this.store.selectSignal(selectFullScreenMediaNames);
    currentMediaUrl = this.store.selectSignal(selectCurrentFullscreenMediaUrl);
    currentMediaUrlIndex = this.store.selectSignal(selectCurrentFullscreenMediaUrlIndex);

    currentWire = this.store.selectSignal(selectSelectedWire)();

    readonly mediaType = computed<MediaType>(() => {
        const isVideo = () => {
            // This is a type guard for Discover wires
            if (this.currentWire && this.currentWire?.Type !== ObjectTypes._DISCOVER_WIRE) return false;
            if (this.currentImageNameOrUrl()?.includes(this.currentWire?.Guid) && this.currentWire?.Properties?.Type === MediaType.VIDEO) {
                return true;
            }
            return VIDEO_EXTENSIONS.some(ext => this.currentImageNameOrUrl()?.toLowerCase()?.endsWith(ext));
        };
        const isImage = () => {
            // This is a type guard for Discover wires
            if (this.currentWire && this.currentWire?.Type !== ObjectTypes._DISCOVER_WIRE) return false;
            if (
                this.currentImageNameOrUrl()?.includes(this.currentWire?.Guid) &&
                (this.currentWire?.Properties?.Type === MediaType.PICTURE || this.currentWire?.Properties?.Type === MediaType.IMAGE)
            ) {
                return true;
            }
            return IMAGE_EXTENSIONS.some(ext => this.currentImageNameOrUrl()?.toLowerCase()?.endsWith(ext));
        };
        if (isVideo()) return MediaType.VIDEO;
        if (isImage()) return MediaType.PICTURE;
        return MediaType.AUDIO;
    });

    readonly currentImageNameOrUrl = computed(() => {
        if (this.currentMediaNames()?.length) {
            const idx = this.currentMediaUrls().findIndex(url => url === this.currentMediaUrl());
            return this.currentMediaNames()[idx];
        }
        return this.currentMediaUrl();
    });

    readonly getImageName = computed(() => {
        let imageName = '';
        const splittedImageUrl = this.currentImageNameOrUrl()?.split('/');
        for (let i = 0; i < splittedImageUrl?.length; i++) {
            if (splittedImageUrl[i].includes('.jpg')) {
                const splittedSubString = splittedImageUrl[i].split('.jpg');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.jpeg')) {
                const splittedSubString = splittedImageUrl[i].split('.jpeg');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.png')) {
                const splittedSubString = splittedImageUrl[i].split('.png');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.mp4')) {
                const splittedSubString = splittedImageUrl[i].split('.mp4');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.webm')) {
                const splittedSubString = splittedImageUrl[i].split('.webm');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.m4v')) {
                const splittedSubString = splittedImageUrl[i].split('.m4v');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.ogv')) {
                const splittedSubString = splittedImageUrl[i].split('.ogv');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.ogg')) {
                const splittedSubString = splittedImageUrl[i].split('.ogg');
                imageName = splittedSubString[0];
            } else if (splittedImageUrl[i].includes('.mkv')) {
                const splittedSubString = splittedImageUrl[i].split('.mkv');
                imageName = splittedSubString[0];
            }
        }
        if (imageName.includes('%2F')) {
            const customSymbolsImageName = imageName.split('%2F');
            imageName = customSymbolsImageName[customSymbolsImageName?.length - 1];
        }
        return imageName;
    });

    readonly mediaExtensionType = computed(() => {
        const segments = this.currentImageNameOrUrl()?.split('.');
        if (!segments?.length) return '';
        const extension = segments[segments?.length - 1];
        return extension;
    });

    readonly mediaTotalCount = computed<number>(() => {
        return this.currentMediaUrls()?.length || 0;
    });

    async closeImageDialog() {
        this.dialogRef.close();
        this.store.dispatch(OpenFullscreenModalActions.clearFullscreenMediaUrls());
    }

    public navigate(direction: NavigationDirection, currentImage: string) {
        this.store.dispatch(OpenFullscreenModalActions.navigateFullscreenMedia({ currentImage, direction }));
    }
}
